/**
 * Citation helper utilities for the Citation Deep-Dive feature.
 * Provides parsing, formatting, and accessibility helpers for citation display.
 */

/**
 * Parse a page string (e.g. "p. 5") and extract the integer page number.
 * Returns 1 for "p. N/A", null, undefined, or empty string.
 *
 * @param {string|null|undefined} pageString
 * @returns {number|null}
 */
export function parsePageNumber(pageString) {
  if (pageString == null || pageString === '') {
    return 1;
  }

  const match = String(pageString).match(/p\.\s*(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }

  return 1;
}

/**
 * Map a confidence score to a badge color.
 * Green for ≥85, blue for 70–84, grey for <70.
 *
 * @param {number} confidence - Value in [0, 100]
 * @returns {string} 'green' | 'blue' | 'grey'
 */
export function getConfidenceBadgeColor(confidence) {
  if (confidence >= 85) return 'green';
  if (confidence >= 70) return 'blue';
  return 'grey';
}

/**
 * Truncate text to maxLength characters, appending an ellipsis if truncated.
 *
 * @param {string} text
 * @param {number} [maxLength=150]
 * @returns {string}
 */
export function truncateText(text, maxLength = 150) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '…';
}

/**
 * Generate a descriptive aria-label for a citation card.
 *
 * @param {string} source - Source document name
 * @param {string} page - Page string (e.g. "p. 5")
 * @returns {string}
 */
export function getCitationAriaLabel(source, page) {
  const pageNum = parsePageNumber(page);
  return `View citation from ${source}, page ${pageNum}`;
}

/**
 * Generate an aria-label for a confidence badge including percentage and level.
 * High is ≥85, medium is 70–84, low is <70.
 *
 * @param {number} confidence - Value in [0, 100]
 * @returns {string}
 */
export function getConfidenceAriaLabel(confidence) {
  let level;
  if (confidence >= 85) {
    level = 'high';
  } else if (confidence >= 70) {
    level = 'medium';
  } else {
    level = 'low';
  }
  return `${confidence} percent confidence, ${level}`;
}

/**
 * Generate aria-live announcement text for page navigation.
 *
 * @param {string} documentName
 * @param {number} pageNumber
 * @returns {string}
 */
export function getPageAnnouncementText(documentName, pageNumber) {
  return `Now viewing page ${pageNumber} of ${documentName}`;
}
